import { z } from 'zod'
import { Refraction, RefractionAction } from '../../schema'
import { OrderVariable, Override, StrapiID } from './common'
import { PricesRegion } from './price'
import { Requirements } from './requirements'
import { StrapiAsset } from './responses'
import { AttributeRelation } from './attribute'

type PricesConfig = PricesRegion & {
  prefix: string | null
  displayOnly: boolean
}

export const BundleType = {
  base: 'base',
  extra: 'extra',
} as const

export type BundleType = (typeof BundleType)[keyof typeof BundleType]

export type RemoteImageProps = {
  // We resolve this from null to undefined on the strapi side to keep it simpler for the client
  contentFit?: 'none' | 'cover' | 'contain' | 'fill'
}

export type OrderConfigItemVariant = {
  id: StrapiID
  price: PricesConfig
  requires: Requirements | null
  image: StrapiAsset | null
  value: string
  condition: string | null
  recommended: string | null
  confirm: string | null
  overrides: Partial<Override>[]
  color: string | null
  colors: StrapiID[]
  attributes?: AttributeRelation[]
  cta: string | null
  translationPath?: string | null
  imageProps?: RemoteImageProps | undefined
}

export type OrderItemType = 'upgrade' | 'base'

export type OrderConfigItem = {
  id: StrapiID
  name: string
  isUpgrade?: boolean
  action?: RefractionAction
  variable: OrderVariable
  base: OrderConfigItemVariant
  requireVariant?: boolean
  variants: OrderConfigItemVariant[]
  type: OrderItemType
}

export type VariableValue = string | number | undefined | null

export type VariableValues = Record<string, VariableValue>

export const INCREMENTAL_VALUE_SEPARATOR = '::'

export const isIncrementalVariableValue = (value?: string | number) => {
  if (typeof value !== 'string') {
    return false
  }
  return value.includes(INCREMENTAL_VALUE_SEPARATOR)
}

export type OrderValues = {
  values: VariableValues
  refraction: Refraction | null | undefined
}

export type ItemWithOrderValues<T> = T & {
  values: OrderValues
}

export const VariableValuesSchema = z.record(
  z.string(),
  z.string().or(z.number()).nullish()
)

/**
 * Use this to make sure we are using the correct variable keys for frames
 */
export const FrameVariableKeys = {
  id: 'frame_id',
  // No frame prefix for compatibility with old code
  vbox: 'vbox',
  price: 'frame_price',
  code: 'frame_code',
  qty: 'frame_qty',
  plc: 'frame_plc',
  type: 'frame_type',
  store_qty: 'frame_store_qty',
  remote_qty: 'frame_remote_qty',
  total_qty: 'frame_total_qty',
  cheapest: 'frame_cheapest',
} as const

// Resolve current cheapest frame index
const getCheapestFrameIndex = (
  all: ItemWithOrderValues<any>
): number | undefined => {
  // Set the first item as the cheapest
  let index = 0
  let minPrice: number | undefined = undefined
  for (let i = 1; i < all.length; i++) {
    const price = all[i].values.values[FrameVariableKeys.price]
    const priceNumber = Number(price)
    // Skip if the price is not a number
    if (!Number.isFinite(priceNumber)) {
      continue
    }
    if (minPrice == undefined || Number(price) < minPrice) {
      index = i
      minPrice = price
    }
  }
  if (minPrice == undefined) {
    return undefined
  }
  return index
}

// Resolve values shared between multiple items
export const getMultiOrderValues = <T>(items: ItemWithOrderValues<T>[]) => {
  const sharedValues: VariableValues = {
    item_count: items.length,
  }

  const cheapestIndex = getCheapestFrameIndex(items)

  return items.map((item, index) => {
    return {
      ...item,
      values: {
        refraction: item.values.refraction,
        values: {
          [FrameVariableKeys.cheapest]:
            cheapestIndex === index ? 'true' : 'false',
          ...sharedValues,
          ...item.values.values,
        },
      },
    }
  })
}
